import { supabase } from './supabaseClient';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const ANON = process.env.REACT_APP_SUPABASE_ANON_KEY;

async function callFn(name, body) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`${SUPABASE_URL}/functions/v1/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: ANON,
      Authorization: `Bearer ${session?.access_token}`,
    },
    body: JSON.stringify(body || {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(json.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.body = json;
    throw err;
  }
  return json;
}

// Streams an SSE generation endpoint. Calls handlers.onDelta(partialJsonString) as chunks
// arrive and resolves with the final "done" payload. Falls through to onError on failure.
async function streamFn(name, body, { onDelta, onDone, onError } = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`${SUPABASE_URL}/functions/v1/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      apikey: ANON,
      Authorization: `Bearer ${session?.access_token}`,
    },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok || !res.body) {
    const j = await res.json().catch(() => ({}));
    const err = new Error(j.error || `HTTP ${res.status}`);
    err.status = res.status;
    if (onError) onError(err);
    throw err;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let result = null;
  let streamErr = null;

  const handleEvent = (evName, dataStr) => {
    let data;
    try { data = JSON.parse(dataStr); } catch { return; }
    if (evName === 'delta') onDelta?.(data.partial);
    else if (evName === 'done') { result = data; onDone?.(data); }
    else if (evName === 'error') { streamErr = new Error(data.error || 'generation failed'); }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const blocks = buf.split('\n\n');
    buf = blocks.pop() ?? '';
    for (const block of blocks) {
      let evName = 'message';
      let dataStr = '';
      for (const line of block.split('\n')) {
        if (line.startsWith('event:')) evName = line.slice(6).trim();
        else if (line.startsWith('data:')) dataStr += line.slice(5).trim();
      }
      if (dataStr) handleEvent(evName, dataStr);
    }
  }

  if (streamErr) { if (onError) onError(streamErr); throw streamErr; }
  return result;
}

// One shared get_me for the whole session — see the note on getMe below.
let mePromise = null;

export const api = {
  async listProjects() {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },
  async createProject(name, userId, { origin = 'user', industryKey = null } = {}) {
    const row = { name, user_id: userId, origin };
    if (industryKey) row.selected_industry_key = industryKey;
    const { data, error } = await supabase
      .from('projects')
      .insert(row)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  generateIdea: (project_id, seed_text, regenerate = false, industry_key = null, lang = null) =>
    callFn('generate-idea', { project_id, seed_text, regenerate, industry_key, lang }),
  streamIdea: (params, handlers) => streamFn('generate-idea', { ...params, stream: true }, handlers),
  suggestSeeds: (params) => callFn('generate-idea', { action: 'suggest_seeds', origin: 'ai', ...params }),
  listIndustries: (lang) => callFn('corpus', { action: 'list_industries', lang }),
  generateMarket: (project_id, regenerate = false) =>
    callFn('generate-market-analysis', { project_id, regenerate }),
  streamMarket: (params, handlers) => streamFn('generate-market-analysis', { ...params, stream: true }, handlers),
  marketDimension: (params) => callFn('generate-market-analysis-dimension', params),
  // Owner-readable (RLS) partial-run state so a reload/disconnect can RESUME missing dimensions
  // without re-running (and re-charging) the skeleton pass. Degrades to null if the row is absent.
  getMarketProgress: async (project_id) => {
    const { data } = await supabase
      .from('market_analysis_progress')
      .select('skeleton, filled')
      .eq('project_id', project_id)
      .maybeSingle();
    return data ?? null;
  },
  generateMarketDetail: (project_id) =>
    callFn('generate-market-analysis-detail', { project_id }),
  // The detail pass is SPLIT into groups (045) and no longer streams: each group is one small
  // call, mirroring marketDimension above. Pass group_index; the response carries groups_total
  // so the caller never has to hardcode how many there are.
  marketDetailGroup: (params) => callFn('generate-market-analysis-detail', params),
  buildPackPlan: (project_id, model_id, prompt_lang) => callFn('generate-build-pack', { project_id, model_id, prompt_lang }),
  buildPackStep: (project_id, n, opts = {}) => callFn('generate-build-pack-step', { project_id, n, ...opts }),

  // Prototype door. Plan = pass 1 (structure + flat fee); screen = pass 2 (per-screen fill /
  // regenerate); files = assembled tree for the sandboxed preview; download streams the zip
  // (assembled on the fly server-side — nothing is stored, re-downloads are free).
  appMockPlan: (project_id, opts = {}) => callFn('generate-app-mock', { project_id, ...opts }),
  appMockScreen: (project_id, screen_id, opts = {}) =>
    callFn('generate-app-mock-screen', { project_id, screen_id, ...opts }),
  appMockFiles: (project_id, theme_key) =>
    callFn('download-app-mock', { project_id, theme_key, as: 'files' }),
  // Bespoke mode: the model authors the screens. shell first, then one call per screen —
  // same loop shape as block mode, so progress/credit UI is unchanged.
  appBespokeShell: (project_id, opts = {}) =>
    callFn('generate-app-bespoke', { project_id, action: 'shell', ...opts }),
  appBespokeScreen: (project_id, screen_id, opts = {}) =>
    callFn('generate-app-bespoke', { project_id, action: 'screen', screen_id, ...opts }),

  appMockDownloadZip: async (project_id, theme_key) => {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(`${SUPABASE_URL}/functions/v1/download-app-mock`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: ANON,
        Authorization: `Bearer ${session?.access_token}`,
      },
      body: JSON.stringify({ project_id, theme_key }),
    });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      const err = new Error(j.error || `HTTP ${res.status}`);
      err.status = res.status;
      throw err;
    }
    return {
      blob: await res.blob(),
      filename: (res.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/)?.[1] || 'prototype.zip',
    };
  },

  uploadPathbDoc: async (file, projectId, userId) => {
    const safe = file.name.replace(/[^\w.\-]+/g, '_');
    const path = `${userId}/${projectId}/${Date.now()}-${safe}`;
    const { error } = await supabase.storage.from('pathb-docs').upload(path, file, { upsert: false });
    if (error) throw error;
    return path;
  },
  pathbIngest: (project_id, storage_path, filename, mime) =>
    callFn('pathb-ingest', { project_id, storage_path, filename, mime }),
  generateSolutionReport: (params) => callFn('generate-solution-report', params),
  generateSolutionReportStep: (project_id, index) => callFn('generate-solution-report-step', { project_id, index }),

  out: (action, params = {}) => callFn('api', { action, ...params }),
  // get_me is asked for by SEVEN callers — the shell, three admin pages, settings twice, and the
  // text-edit provider — and every one of them fires on mount. They were separate round trips, so a
  // single menu click paid for the same answer several times over, and each extra in-flight request
  // is another chance for a transient failure to hit a page that redirects to the dashboard when it
  // cannot confirm you are an admin. Ali, 2026-08-16: "it takes a lot of time to load the page and
  // sometimes it jumps to dashboard."
  //
  // Concurrent callers share one promise; the answer is cached for the session because none of it
  // changes without a reload. `refreshMe()` exists for the one place that edits the profile.
  getMe: () => {
    if (!mePromise) {
      mePromise = callFn('api', { action: 'get_me' }).catch((e) => {
        // A failure must NOT be cached: the next caller has to be able to try again, or one blip at
        // start-up would keep the app in a broken state until reload.
        mePromise = null;
        throw e;
      });
    }
    return mePromise;
  },
  refreshMe: () => { mePromise = null; return api.getMe(); },
  setLanguage: (language) => callFn('api', { action: 'set_language', language }),
  setProfile: (profile) => callFn('api', { action: 'set_profile', ...profile }),
  setDefaultModel: (model_id) => callFn('api', { action: 'set_default_model', model_id }),
  getWallet: () => callFn('api', { action: 'get_wallet' }),
  getHomeSummary: () => callFn('api', { action: 'get_home_summary' }),
  walletPreview: (params = {}) => callFn('api', { action: 'wallet_preview', ...params }),
  // Self-serve purchase (IDPay behind the gateway-agnostic seam). Rial only — no USD in any of these.
  walletBundles: () => callFn('wallet', { action: 'list_bundles' }),
  walletCreateCheckout: (bundle_id) => callFn('wallet', { action: 'create_checkout', bundle_id }),
  walletVerifyCheckout: (order_id) => callFn('wallet', { action: 'verify_checkout', order_id }),
  listSelectableModels: () => callFn('api', { action: 'list_selectable_models' }),
  modelKeyAvailability: () => callFn('api', { action: 'model_key_availability' }),
  // Names + tiers, no key lookups — used to paint pickers instantly.
  listModelsFast: () => callFn('api', { action: 'list_models_fast' }),
  corpusAdmin: (action, params = {}) => callFn('corpus-admin', { action, ...params }),
  desk: (action, params = {}) => callFn('desk', { action, ...params }),
  getManifest: () => callFn('get-tenant-manifest', {}),
  tenantAdmin: (action, params = {}) => callFn('tenant-admin', { action, ...params }),
  uiText: (action, params = {}) => callFn('ui-text', { action, ...params }),

  // Providers config layer (super-admin). Direct SECURITY-DEFINER RPCs (is_admin-gated); the API
  // key is write-only to Vault and never returned.
  listModelProviders: async () => {
    const { data, error } = await supabase.rpc('list_model_providers');
    if (error) throw error;
    return data || [];
  },
  upsertModelProvider: async ({ name, adapter_type, base_url, api_key }) => {
    const { error } = await supabase.rpc('upsert_model_provider', {
      p_name: name, p_adapter_type: adapter_type, p_base_url: base_url || null, p_api_key: api_key || null,
    });
    if (error) throw error;
  },
  setModelProviderActive: async (name, active) => {
    const { error } = await supabase.rpc('set_model_provider_active', { p_name: name, p_active: active });
    if (error) throw error;
  },
  // Platform DEFAULT model (fallback identity only — provider + model_id + prompt_caching, never a key).
  getPlatformDefault: async () => {
    const { data, error } = await supabase.rpc('get_platform_default');
    if (error) throw error;
    return data || {};
  },
  setPlatformDefault: async ({ provider, model_id, prompt_caching }) => {
    const { error } = await supabase.rpc('set_platform_default', {
      p_provider: provider, p_model_id: model_id, p_prompt_caching: !!prompt_caching,
    });
    if (error) throw error;
  },

  // Rule 8 — QA config (super-admin). is_admin-gated SECURITY-DEFINER RPCs (migration 041).
  getQaConfig: async () => {
    const { data, error } = await supabase.rpc('get_qa_config');
    if (error) throw error;
    return data || {};
  },
  setQaConfig: async ({ qa_tenant_id, qa_model_id }) => {
    const { error } = await supabase.rpc('set_qa_config', {
      p_qa_tenant_id: qa_tenant_id || null, p_qa_model_id: qa_model_id || null,
    });
    if (error) throw error;
  },

  // Per-user model picker + BYO provider keys (non-admin Settings). Direct SECURITY-DEFINER RPCs
  // (auth'd to the current user, migration 040). Keys are write-only to Vault, never returned.
  listSelectableModelsFull: async () => {
    const { data, error } = await supabase.rpc('list_selectable_models_full');
    if (error) throw error;
    return data || [];
  },
  listUserProviderKeys: async () => {
    const { data, error } = await supabase.rpc('list_user_provider_keys');
    if (error) throw error;
    return data || [];
  },
  upsertUserProviderKey: async (provider, api_key) => {
    const { data, error } = await supabase.rpc('upsert_user_provider_key', {
      p_provider: provider, p_api_key: api_key || null,
    });
    if (error) throw error;
    return data || {};
  },
};
